import { draftSenderPersonaPlaceholder } from 'owa-locstrings/lib/strings/draftsenderpersonaplaceholder.locstring.json';
import loc from 'owa-localize';
import getSenderDisplayName from '../utils/getSenderDisplayName';
import { highlightTermsInHtmlElement } from 'owa-mail-highlight-terms';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { MailIcons } from 'owa-mail-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isEmailAddressDecoratedBrand } from 'owa-persona';
import MessageSafetyLevel from 'owa-service/lib/contract/MessageSafetyLevel';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

import * as React from 'react';
import {
    useLivePersonaCard,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';

const styles = require('./SenderPersona.scss');
const classNames = require('classnames/bind').bind(styles);

export interface SenderPersonaProps {
    sender: SingleRecipientType;
    isRead: boolean;
    treatAsDraft: boolean;
    displaySelf: boolean;
    hideSMTP?: boolean;
    messageSafetyLevel: MessageSafetyLevel;
    isUnauthenticatedSender?: boolean;
    className?: string;
    showPlainText?: boolean;
    displayYouForSelf?: boolean;
}

export interface SenderPersonaState {
    showPlainText: boolean;
}

interface SenderPersonaDivWithPersonaCardBehaviorProps extends PersonaCardBehaviorProps {
    displayNameComponent: JSX.Element;
}

const timeoutForShowPlainText = 1;

@observer
export default class SenderPersona extends React.Component<SenderPersonaProps, SenderPersonaState> {
    constructor(props: SenderPersonaProps) {
        super(props);
        this.state = {
            showPlainText: true,
        };
    }

    componentDidMount() {
        // If this is for draft, no need to re-render.
        if (!this.props.treatAsDraft) {
            setTimeout(() => {
                this.setState({ showPlainText: false });
            }, timeoutForShowPlainText);
        }
    }

    render() {
        const userConfiguration = getUserConfiguration();
        const {
            sender,
            isRead,
            displaySelf,
            treatAsDraft,
            hideSMTP,
            className,
            messageSafetyLevel,
            isUnauthenticatedSender,
            displayYouForSelf,
        } = this.props;
        let senderName = '';
        let emailAddress = null;
        let mailboxType = '';
        if (sender && sender.Mailbox) {
            senderName = sender.Mailbox.Name;
            emailAddress = sender.Mailbox.EmailAddress;
            mailboxType = sender.Mailbox.MailboxType;
        }
        if (displaySelf) {
            // If displaying self, override senderName and emailAddress using userConfiguration data
            senderName = userConfiguration.SessionSettings.UserDisplayName;
            emailAddress = userConfiguration.SessionSettings.UserEmailAddress;
        }

        if (treatAsDraft) {
            return (
                <div className={classNames(className, styles.senderPersona, styles.isDraft)}>
                    {loc(draftSenderPersonaPlaceholder)}
                </div>
            );
        } else {
            const displayName = getSenderDisplayName(
                senderName,
                emailAddress,
                mailboxType,
                hideSMTP,
                messageSafetyLevel,
                displayYouForSelf
            );
            const displayNameComponent = renderDisplayName(
                emailAddress,
                displayName,
                isMessageLevelUntrusted(messageSafetyLevel)
            );
            const senderPersonaClassName = classNames(className, styles.senderPersona, {
                isUnread: !isRead,
            });

            return this.state.showPlainText || this.props.showPlainText ? (
                <div className={className}>{displayNameComponent}</div>
            ) : (
                <SenderPersonaDivWithPersonaCardBehavior
                    emailAddress={emailAddress}
                    name={senderName}
                    isUnauthenticatedSender={isUnauthenticatedSender}
                    displayNameComponent={displayNameComponent}
                    className={senderPersonaClassName}
                    clientScenario="SenderPersonaDiv"
                />
            );
        }
    }
}

function SenderPersonaDivWithPersonaCardBehavior(
    props: SenderPersonaDivWithPersonaCardBehaviorProps
) {
    const PersonaCardBehavior = useLivePersonaCard(props);
    const { displayNameComponent, className } = props;
    return (
        <PersonaCardBehavior>
            <div
                ref={ref => {
                    highlightTermsInHtmlElement(ref);
                }}
                className={className}>
                {displayNameComponent}
            </div>
        </PersonaCardBehavior>
    );
}

function renderDisplayName(
    emailAddress: string,
    displayName: string[],
    isUntrusted: boolean
): JSX.Element {
    return (
        <span>
            {displayName[0]}
            {!isUntrusted &&
                isFeatureEnabled('rp-brandCards') &&
                isFeatureEnabled('rp-brandDecorations') &&
                isEmailAddressDecoratedBrand(emailAddress) && (
                    <Icon iconName={MailIcons.VerifiedBrand} className={styles.brandIcon} />
                )}
            {displayName.length > 1 && displayName[1]}
        </span>
    );
}

function isMessageLevelUntrusted(messageSafetyLevel: MessageSafetyLevel): boolean {
    return (
        messageSafetyLevel === MessageSafetyLevel.Suspicious ||
        messageSafetyLevel === MessageSafetyLevel.Unknown
    );
}
